import { Button } from '@carbon/react';
import { useState, useEffect } from 'react';
import {
  Notification,
  Close,
  CheckmarkOutline,
  WarningAlt,
  Calendar,
  Money,
  Time,
  Information,
} from '@carbon/icons-react';
import { useFetcher, Link } from 'react-router';
import { formatDistanceToNow } from 'date-fns';

type NotificationData = {
  id: string;
  type: string;
  category: string;
  title: string;
  message: string;
  link: string | null;
  isRead: boolean;
  priority: string;
  createdAt: Date | string;
  sender: {
    id: string;
    firstName: string | null;
    lastName: string | null;
    username: string;
  } | null;
};

export default function InboxDrawer({
  isOpen,
  onClose,
  userId,
  notifications,
  unreadCount,
  isAdminOrHR = false,
}: {
  isOpen: boolean;
  onClose: () => void;
  userId: string;
  notifications: NotificationData[];
  unreadCount: number;
  isAdminOrHR?: boolean;
}) {
  const fetcher = useFetcher();
  const [activeTab, setActiveTab] = useState<'all' | 'actions'>('all');

  // Helper function to get current quincena date range
  const getCurrentQuincenaRange = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    const day = now.getDate();

    let startDate: Date;
    let endDate: Date;

    if (day <= 15) {
      // Quincena 1: 1st to 15th
      startDate = new Date(year, month, 1, 0, 0, 0, 0);
      endDate = new Date(year, month, day, 23, 59, 59, 999);
    } else {
      // Quincena 2: 16th to end of month
      startDate = new Date(year, month, 16, 0, 0, 0, 0);
      endDate = new Date(year, month, day, 23, 59, 59, 999);
    }

    return { startDate, endDate };
  };

  // Filter logic for tabs
  const filteredNotifications = notifications.filter((notif) => {
    // Apply tab-specific filters
    if (activeTab === 'actions')
      return notif.category === 'action_required';

    // 'all' tab: show all unread + read notifications from current quincena
    if (activeTab === 'all') {
      // Always show unread notifications regardless of date
      if (!notif.isRead) return true;

      // For read notifications, only show if within current quincena
      const { startDate, endDate } = getCurrentQuincenaRange();
      const notifDate = new Date(notif.createdAt);
      return notifDate >= startDate && notifDate <= endDate;
    }

    return true;
  });

  // Mark notification as read when clicked
  const handleMarkAsRead = (notificationId: string) => {
    const formData = new FormData();
    formData.append('intent', 'mark_read');
    formData.append('notificationId', notificationId);
    fetcher.submit(formData, {
      method: 'POST',
      action: '/api/notifications',
    });
  };

  // Mark all as read
  const handleMarkAllAsRead = () => {
    const formData = new FormData();
    formData.append('intent', 'mark_all_read');
    formData.append('userId', userId);
    fetcher.submit(formData, {
      method: 'POST',
      action: '/api/notifications',
    });
  };

  // Get icon based on notification type
  const getNotificationIcon = (type: string, priority: string) => {
    // Leave-related
    if (type.startsWith('leave_')) {
      return (
        <div className="w-8 h-8 rounded-sm bg-blue-500/10 text-blue-500 flex items-center justify-center border border-blue-500/20">
          <Calendar size={16} />
        </div>
      );
    }

    // Payroll/payout
    if (type.startsWith('payroll_') || type.startsWith('payout_')) {
      return (
        <div className="w-8 h-8 rounded-sm bg-green-500/10 text-green-500 flex items-center justify-center border border-green-500/20">
          <Money size={16} />
        </div>
      );
    }

    // Time tracking
    if (type.startsWith('time_') || type.startsWith('timesheet_')) {
      return (
        <div className="w-8 h-8 rounded-sm bg-purple-500/10 text-purple-500 flex items-center justify-center border border-purple-500/20">
          <Time size={16} />
        </div>
      );
    }

    // High priority / urgent
    if (priority === 'urgent' || priority === 'high') {
      return (
        <div className="w-8 h-8 rounded-sm bg-amber-500/10 text-amber-500 flex items-center justify-center border border-amber-500/20">
          <WarningAlt size={16} />
        </div>
      );
    }

    // Default
    return (
      <div className="w-8 h-8 rounded-sm bg-gray-500/10 text-gray-500 flex items-center justify-center border border-gray-500/20">
        <Information size={16} />
      </div>
    );
  };

  // Format time ago
  const formatTimeAgo = (date: Date | string) => {
    try {
      return formatDistanceToNow(new Date(date), { addSuffix: true });
    } catch {
      return 'recently';
    }
  };

  return (
    <div className="flex flex-col h-full! bg-card w-full!">
      {/* INBOX HEADER */}
      <div className="p-6 border-b border-border flex justify-between items-center bg-background">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-black text-foreground tracking-tight">
            Inbox
          </h2>
          {unreadCount > 0 && (
            <span className="bg-primary-base/10 text-primary-base text-xs font-bold px-2 py-0.5 rounded-sm">
              {unreadCount} New
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <button
            onClick={handleMarkAllAsRead}
            disabled={fetcher.state === 'submitting'}
            className="text-xs font-bold text-muted-foreground hover:text-primary-base transition-colors disabled:opacity-50"
          >
            {fetcher.state === 'submitting' ? 'Marking...' : 'Mark all read'}
          </button>
          <button
            onClick={onClose}
            className="p-1 text-muted-foreground hover:text-foreground transition-colors border border-transparent hover:border-border rounded-sm"
          >
            <Close size={20} />
          </button>
        </div>
      </div>

      {/* INBOX TABS */}
      <div className="flex border-b border-border bg-background px-6 gap-6">
        {[
          { id: 'all', label: 'All Activity' },
          { id: 'actions', label: 'Action Required' },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`py-4 text-xs font-bold uppercase tracking-wider transition-colors relative ${
              activeTab === tab.id
                ? 'text-primary-base'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.label}
            {activeTab === tab.id && (
              <span className="absolute bottom-0 left-0 w-full h-0.5 bg-primary-base" />
            )}
          </button>
        ))}
      </div>

      {/* NOTIFICATION LIST */}
      <div className="flex-1 overflow-y-auto bg-card custom-scrollbar">
        {filteredNotifications.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground">
            <Notification size={32} className="mx-auto mb-4 opacity-50" />
            <p className="text-sm font-bold uppercase tracking-wider">
              All caught up
            </p>
            <p className="text-xs mt-1">No new notifications in this view.</p>
          </div>
        ) : (
          <div className="divide-y divide-border">
            {filteredNotifications.map((notif) => {
              const senderName = notif.sender
                ? `${notif.sender.firstName ?? ''} ${notif.sender.lastName ?? ''}`.trim() ||
                  notif.sender.username
                : 'System';

              // Determine the correct link, notification type badge, and badge color
              let finalLink: string | null = null;
              let isOpsItem = false;
              let badgeLabel = '';
              let badgeColor = '';

              if (isAdminOrHR) {
                // Determine if this is an ops item (action_required or admin_ops categories)
                isOpsItem =
                  notif.category === 'action_required' ||
                  notif.category === 'admin_ops';

                // Set badge for admin/HR
                if (notif.category === 'action_required' || notif.category === 'admin_ops') {
                  badgeLabel = 'OPS';
                  badgeColor = 'bg-blue-500/10 text-blue-500';
                } else if (notif.category === 'personal') {
                  badgeLabel = 'MY';
                  badgeColor = 'bg-green-500/10 text-green-500';
                } else if (notif.category === 'team') {
                  badgeLabel = 'TEAM';
                  badgeColor = 'bg-purple-500/10 text-purple-500';
                } else if (notif.category === 'company_wide') {
                  badgeLabel = 'ALL';
                  badgeColor = 'bg-amber-500/10 text-amber-500';
                }

                // For admin/HR: route based on category
                if (notif.category === 'personal' || notif.category === 'team') {
                  // Personal and team notifications go to /me/inbox
                  const isLeaveRelated =
                    notif.type === 'leave_request_submitted' ||
                    notif.type === 'leave_request_approved' ||
                    notif.type === 'leave_request_rejected' ||
                    notif.type === 'leave_request_cancelled' ||
                    notif.type === 'approval_required';

                  if (isLeaveRelated) {
                    // Check if this is about the user's own leave or a teammate's leave
                    const isOwnLeave = notif.sender?.id === userId;
                    const filterParam = isOwnLeave ? 'my_leaves' : 'team_leaves';
                    finalLink = `/me/inbox?filter=${filterParam}`;
                  } else {
                    finalLink = '/me/inbox';
                  }
                } else if (notif.category === 'action_required' || notif.category === 'admin_ops') {
                  // OPS notifications: use the specific link if available
                  finalLink = notif.link || '/ops/inbox';
                } else {
                  // company_wide notifications go to /ops/inbox
                  finalLink = '/ops/inbox';
                }
              } else {
                // For regular employees: set badge based on category
                if (notif.category === 'personal') {
                  badgeLabel = 'MY';
                  badgeColor = 'bg-green-500/10 text-green-500';
                } else if (notif.category === 'team') {
                  badgeLabel = 'TEAM';
                  badgeColor = 'bg-purple-500/10 text-purple-500';
                } else if (notif.category === 'company_wide') {
                  badgeLabel = 'ALL';
                  badgeColor = 'bg-amber-500/10 text-amber-500';
                }

                // For employees: all notifications go to /me/inbox with smart category filtering
                const isLeaveRelated =
                  notif.type === 'leave_request_submitted' ||
                  notif.type === 'leave_request_approved' ||
                  notif.type === 'leave_request_rejected' ||
                  notif.type === 'leave_request_cancelled' ||
                  notif.type === 'approval_required';

                if (isLeaveRelated) {
                  // Check if this is about the user's own leave or a teammate's leave
                  const isOwnLeave = notif.sender?.id === userId;
                  const filterParam = isOwnLeave ? 'my_leaves' : 'team_leaves';
                  finalLink = `/me/inbox?filter=${filterParam}`;
                } else {
                  finalLink = '/me/inbox';
                }
              }

              const NotificationContent = (
                <div
                  className={`p-6 flex gap-4 transition-colors hover:bg-muted/5 group relative cursor-pointer ${
                    !notif.isRead ? 'bg-muted/5' : 'bg-card'
                  }`}
                  onClick={() => {
                    if (!notif.isRead) {
                      handleMarkAsRead(notif.id);
                    }
                    if (finalLink) {
                      onClose();
                    }
                  }}
                >
                  {/* Unread Indicator */}
                  {!notif.isRead && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary-base" />
                  )}

                  {/* Icon Column */}
                  <div className="shrink-0 mt-1">
                    {getNotificationIcon(notif.type, notif.priority)}
                  </div>

                  {/* Content Column */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex items-center gap-2">
                        <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                          {notif.title}
                        </p>
                        {/* Category badge - show for all tabs */}
                        {badgeLabel && (
                          <span
                            className={`text-xs font-bold px-1.5 py-0.5 rounded-sm ${badgeColor}`}
                          >
                            {badgeLabel}
                          </span>
                        )}
                      </div>
                      <span className="text-xs font-mono font-bold text-muted-foreground shrink-0 ml-4">
                        {formatTimeAgo(notif.createdAt)}
                      </span>
                    </div>

                    <h4 className="text-sm font-bold text-foreground mb-1">
                      {senderName}
                    </h4>

                    <p className="text-sm text-muted-foreground leading-relaxed">
                      {notif.message}
                    </p>
                  </div>
                </div>
              );

              // Wrap with Link if we have a final link
              return finalLink ? (
                <Link key={notif.id} to={finalLink}>
                  {NotificationContent}
                </Link>
              ) : (
                <div key={notif.id}>{NotificationContent}</div>
              );
            })}
          </div>
        )}
      </div>

      {/* INBOX FOOTER */}
      <div className="p-4 border-t border-border bg-background text-center">
        {isAdminOrHR ? (
          <div className="flex items-center justify-center gap-6">
            <Link
              to="/me/inbox"
              onClick={onClose}
              className="text-xs font-bold text-primary-base uppercase tracking-wider hover:underline"
            >
              View My Inbox →
            </Link>
            <span className="text-muted-foreground">•</span>
            <Link
              to="/ops/inbox"
              onClick={onClose}
              className="text-xs font-bold text-primary-base uppercase tracking-wider hover:underline"
            >
              View Ops Inbox →
            </Link>
          </div>
        ) : (
          <Link
            to="/me/inbox"
            onClick={onClose}
            className="text-xs font-bold text-primary-base uppercase tracking-wider hover:underline"
          >
            View My Inbox →
          </Link>
        )}
      </div>
    </div>
  );
}
